import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AICoachingMessage, LiveUtterance, TranscriptEntry, TysonCoachingContextData } from '../types/coaching';
import { useTysonRealtimeCoaching } from '../hooks/useTysonRealtimeCoaching';

interface TysonCoachingContextValue {
	coachingMessages: AICoachingMessage[];
	conversationId: string | null;
	isCreatingConversation: boolean;
	isAIResponding: boolean;
	isActive: boolean;
	contextData: TysonCoachingContextData | null;
	// Historical conversation viewing
	viewingHistoricalConversationId: string | null;
	viewHistoricalConversation: (conversationId: string) => void;
	clearHistoricalConversation: () => void;
	startCoaching: (contextData: TysonCoachingContextData) => Promise<void>;
	sendTranscriptEntry: (entry: TranscriptEntry) => void;
	sendTranscriptBatchImmediately: (entries: TranscriptEntry[]) => void;
	sendEndOfCallSummary: () => void;
	sendDirectQuestion: (question: string) => void;
	stopCoaching: () => void;
	// Live utterances for display
	liveUtterances: LiveUtterance[];
	// Set live utterances (called by RealCallCoachingBridge)
	setLiveUtterances: (utterances: LiveUtterance[]) => void;
	// Manual suggestion request (for real calls)
	requestSuggestion: () => void;
	// Register flush callback from real call hook
	registerFlushCallback: (callback: (() => TranscriptEntry[]) | null) => void;
}

const TysonCoachingContext = createContext<TysonCoachingContextValue | null>(null);

export const TysonCoachingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isActive, setIsActive] = useState(false);
	const [contextData, setContextData] = useState<TysonCoachingContextData | null>(null);
	const [viewingHistoricalConversationId, setViewingHistoricalConversationId] = useState<string | null>(null);
	const [liveUtterances, setLiveUtterancesState] = useState<LiveUtterance[]>([]);
	const [tysonPendingEntries, setTysonPendingEntries] = useState<TranscriptEntry[]>([]);
	const flushCallbackRef = useRef<(() => TranscriptEntry[]) | null>(null);

	const setLiveUtterances = useCallback((utterances: LiveUtterance[]) => {
		setLiveUtterancesState(utterances);
	}, []);

	const coaching = useTysonRealtimeCoaching(contextData);

	const startCoaching = async (data: TysonCoachingContextData) => {
		// Clear any historical conversation view when starting a new session
		setViewingHistoricalConversationId(null);
		setContextData(data);
		setIsActive(true);
		// Pass data directly to avoid race condition with async state update
		await coaching.createConversation(data);
	};

	const stopCoaching = () => {
		setIsActive(false);
		setContextData(null);
		coaching.resetCoaching();
	};

	const viewHistoricalConversation = (conversationId: string) => {
		setViewingHistoricalConversationId(conversationId);
	};

	const clearHistoricalConversation = () => {
		setViewingHistoricalConversationId(null);
	};

	const registerFlushCallback = (callback: (() => TranscriptEntry[]) | null) => {
		flushCallbackRef.current = callback;
	};

	// Wrapper to accumulate entries for manual flush AND display in coaching panel
	const handleSendTranscriptEntry = useCallback(
		(entry: TranscriptEntry) => {
			// Accumulate for sending when "Get Suggestion" is clicked
			setTysonPendingEntries(prev => [...prev, entry]);

			// Add to liveUtterances for display in coaching panel
			const utterance: LiveUtterance = {
				sequenceId: `tyson-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
				speaker: entry.speaker as 'Lead' | 'Sales Rep',
				transcription: entry.message,
				final: true, // Tyson transcripts are always final (complete messages)
				timestamp: entry.timestamp,
			};
			setLiveUtterancesState(prev => [...prev, utterance]);

			// Still queue in hook for backward compat
			coaching.sendTranscriptEntry(entry);
		},
		[coaching]
	);

	const requestSuggestion = () => {
		// For real calls: use the registered flush callback
		if (flushCallbackRef.current) {
			const entries = flushCallbackRef.current();
			if (entries && entries.length > 0) {
				coaching.sendTranscriptBatchImmediately(entries);
			}
			return;
		}

		// For Tyson practice calls: flush accumulated entries
		if (tysonPendingEntries.length > 0) {
			coaching.sendTranscriptBatchImmediately(tysonPendingEntries);
			setTysonPendingEntries([]);
			setLiveUtterancesState([]); // Clear display after sending
		}
	};

	const value: TysonCoachingContextValue = {
		coachingMessages: coaching.coachingMessages,
		conversationId: coaching.conversationId,
		isCreatingConversation: coaching.isCreatingConversation,
		isAIResponding: coaching.isAIResponding,
		isActive,
		contextData,
		viewingHistoricalConversationId,
		viewHistoricalConversation,
		clearHistoricalConversation,
		startCoaching,
		sendTranscriptEntry: handleSendTranscriptEntry,
		sendTranscriptBatchImmediately: coaching.sendTranscriptBatchImmediately,
		sendEndOfCallSummary: coaching.sendEndOfCallSummary,
		sendDirectQuestion: coaching.sendDirectQuestion,
		stopCoaching,
		// Live utterances for real call coaching
		liveUtterances,
		setLiveUtterances,
		requestSuggestion,
		registerFlushCallback,
	};

	return <TysonCoachingContext.Provider value={value}>{children}</TysonCoachingContext.Provider>;
};

export const useTysonCoaching = (): TysonCoachingContextValue => {
	const context = useContext(TysonCoachingContext);
	if (!context) {
		throw new Error('useTysonCoaching must be used within a TysonCoachingProvider');
	}
	return context;
};
